import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';

import { useToast } from '@client/components/hooks/use-toast';
import {
  selectAdvisorHierarchyState,
  selectSessionAdvisor,
  selectSupervisorAdvisorRelationships,
} from '@client/model/selectors';
import { fetchAdvisorHierarchyRelationshipsByAdvisorIdRequest } from '@client/model/advisorHierarchyModel';
import { clientsApi } from '@client/api/waterlily';
import { isClientUnderNewPartnerConsent } from '@shared';
import { useIsEdjPilotAdvisor } from '@/shared/hooks/use-edj-pilot-advisor';
import { useDistributionPartnerLegacyShareConsent } from '@/features/distribution-partners';

import { useShareClientWithSupervisors } from './use-share-client';

export const MAX_NOTE_LENGTH = 2000;

export function getClientDisplayName(c: Client): string {
  if (c.clientFirstName && c.clientLastName) {
    return `${c.clientFirstName} ${c.clientLastName}`;
  }
  return c.clientEmail || 'this client';
}

export function getRecipientDisplayName(rel: SupervisorAdvisorRelationship): string {
  const name =
    rel.advisorFirstName && rel.advisorLastName
      ? `${rel.advisorFirstName} ${rel.advisorLastName}`
      : null;
  return name ?? rel.advisorEmail;
}

export function getRecipientOrgName(rel: SupervisorAdvisorRelationship): string | null {
  return rel.organizationDisplayName || rel.organizationName || null;
}

function matchesSearch(rel: SupervisorAdvisorRelationship, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  const haystack = [
    rel.advisorFirstName,
    rel.advisorLastName,
    rel.advisorEmail,
    rel.organizationDisplayName,
    rel.organizationName,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

interface SuccessToastArgs {
  recipientCount: number;
  clientCount: number;
}

interface UseShareClientDialogOptions<TStep extends string> {
  client: Client;
  partner?: Client | null;
  open: boolean;
  onClose: () => void;
  initialStep: TStep;
  successToast: (args: SuccessToastArgs) => { title: string; description: string };
  errorToastTitle: string;
}

// Shared state and behaviour behind both per-client share dialogs: the base "Share Client" flow and
// the Edward Jones "Collaborate on this case" flow. The two differ in copy and in which steps they
// walk through, so each owns its own step names — everything else (recipient loading, search,
// selection, the share mutation and its reset-on-open) lives here.
export function useShareClientDialog<TStep extends string>({
  client,
  partner,
  open,
  onClose,
  initialStep,
  successToast,
  errorToastTitle,
}: UseShareClientDialogOptions<TStep>) {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const sessionAdvisor = useSelector(selectSessionAdvisor);
  const supervisors = useSelector(selectSupervisorAdvisorRelationships);
  const { relationshipsFetchStatus } = useSelector(selectAdvisorHierarchyState);

  const isEdjPilot = useIsEdjPilotAdvisor();
  const { hasLegacyShareConsent } = useDistributionPartnerLegacyShareConsent();

  const hasPartner = !!partner && !partner.isSampleClient;

  const [step, setStep] = useState<TStep>(initialStep);
  const [search, setSearch] = useState('');
  // The selection holds the recipient records themselves, not just their ids, so what an
  // acknowledgement step displays is exactly what the share request submits — a background refresh
  // of the hierarchy list can't make the two drift apart mid-dialog.
  const [selectedRecipients, setSelectedRecipients] = useState<SupervisorAdvisorRelationship[]>([]);
  const [includePartner, setIncludePartner] = useState(false);
  const [note, setNote] = useState('');
  const [attestationOpen, setAttestationOpen] = useState(false);

  // Sharing a legacy (pre-cutoff) client requires the advisor to attest they hold the consents to
  // share it — unless they've already done the batch "share all earlier clients" attestation. EDJ
  // advisors have no legacy flow.
  const needsLegacyAttestation =
    !isEdjPilot &&
    !hasLegacyShareConsent &&
    !isClientUnderNewPartnerConsent(client.clientAddedDateTime);

  const { shareWithSupervisors, isPending } = useShareClientWithSupervisors(client.clientId);

  const sharedSupervisorsQuery = useQuery({
    queryKey: ['clientSharedSupervisors', client.clientId],
    queryFn: () => clientsApi.getSharedSupervisors(client.clientId),
    enabled: open && !!client.clientId,
  });

  const advisorId = sessionAdvisor?.advisorId;

  useEffect(() => {
    if (open && advisorId && relationshipsFetchStatus === 'idle') {
      dispatch(fetchAdvisorHierarchyRelationshipsByAdvisorIdRequest({ advisorId }));
    }
  }, [open, advisorId, relationshipsFetchStatus, dispatch]);

  useEffect(() => {
    if (open) {
      setStep(initialStep);
      setSearch('');
      setSelectedRecipients([]);
      setIncludePartner(false);
      setNote('');
      setAttestationOpen(false);
    }
  }, [open, initialStep]);

  const filteredRecipients = useMemo(() => {
    if (!supervisors) return [];

    return supervisors.filter(rel => matchesSearch(rel, search));
  }, [supervisors, search]);

  // Recipients who can already see this client — surfaced with an "Already has access" badge so the
  // advisor knows, but left unchecked so re-sharing (and its email) is always a deliberate pick.
  const alreadySharedIds = useMemo(
    () => new Set(sharedSupervisorsQuery.data?.supervisorAdvisorIds ?? []),
    [sharedSupervisorsQuery.data],
  );

  const selectedIds = useMemo(
    () => selectedRecipients.map(rel => rel.advisorId),
    [selectedRecipients],
  );

  const isLoadingRecipients = relationshipsFetchStatus === 'loading';
  const hasNoRecipients = !isLoadingRecipients && (supervisors?.length ?? 0) === 0;

  const toggleRecipient = (recipient: SupervisorAdvisorRelationship) => {
    setSelectedRecipients(prev =>
      prev.some(rel => rel.advisorId === recipient.advisorId)
        ? prev.filter(rel => rel.advisorId !== recipient.advisorId)
        : [...prev, recipient],
    );
  };

  const handleClose = () => {
    if (isPending) return;

    onClose();
  };

  const shouldIncludePartner = hasPartner && includePartner;

  const share = () => {
    shareWithSupervisors(
      {
        supervisorAdvisorIds: selectedIds,
        note: note.trim().length > 0 ? note.trim() : null,
        alsoSharePartner: shouldIncludePartner,
      },
      {
        onSuccess: () => {
          toast({
            ...successToast({
              recipientCount: selectedIds.length,
              clientCount: shouldIncludePartner ? 2 : 1,
            }),
            className: '-translate-y-5',
          });
          onClose();
        },
        onError: err => {
          toast({
            title: errorToastTitle,
            description:
              err instanceof Error ? err.message : 'Something went wrong. Please try again.',
            variant: 'destructive',
            className: '-translate-y-5',
          });
        },
      },
    );
  };

  return {
    step,
    setStep,
    search,
    setSearch,
    selectedIds,
    selectedRecipients,
    toggleRecipient,
    filteredRecipients,
    alreadySharedIds,
    isLoadingRecipients,
    hasNoRecipients,
    hasPartner,
    includePartner,
    setIncludePartner,
    note,
    setNote,
    attestationOpen,
    setAttestationOpen,
    needsLegacyAttestation,
    shouldIncludePartner,
    isPending,
    share,
    handleClose,
    clientDisplayName: getClientDisplayName(client),
    partnerDisplayName: partner ? getClientDisplayName(partner) : null,
  };
}
